import * as DialogPrimitive from '@radix-ui/react-dialog'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/use-toast'

// Renders the active toast() call as a blocking, centered alert modal instead
// of a corner toast — errors were getting missed because toasts auto-dismissed
// before anyone read them. Portaled to <body> with a z-index above every other
// overlay in the app (Dialog/Sheet top out at z-[10000]) so an error raised
// while another modal/sheet is open always surfaces on top of it, and stays
// until the user dismisses it (no auto-timeout).
export function Toaster() {
  const { toasts, dismiss } = useToast()
  const current = toasts[0]

  if (!current) return null

  const isError = current.variant === 'destructive'

  return (
    <DialogPrimitive.Root
      open={!!current.open}
      onOpenChange={(open) => {
        if (!open) dismiss(current.id)
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[999998] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-[999999] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-background p-5 shadow-xl duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                isError ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
              )}
            >
              {isError ? <AlertCircle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1 pt-1">
              <DialogPrimitive.Title className="text-sm font-semibold leading-5 text-foreground">
                {current.title ?? (isError ? 'Something went wrong' : 'Success')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                className={cn('mt-1 text-sm text-muted-foreground', !current.description && 'sr-only')}
              >
                {current.description ?? 'Details unavailable.'}
              </DialogPrimitive.Description>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            {current.action}
            <DialogPrimitive.Close
              className={cn(
                'rounded-lg px-4 py-1.5 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
                isError ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              )}
            >
              OK
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
